import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { environment } from "../../environments/environment";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";

import CourseSection, {
  CourseSectionLecture,
  SectionLecture,
} from "../models/course-content.model";

@Injectable({
  providedIn: "root",
})
export class CourseContentService {
  visibility = environment.visibility;
  userLogin: User;
  private dbPath = "/courses";

  sectionsRef: AngularFirestoreCollection<CourseSection>;
  lecturesRef: AngularFirestoreCollection<CourseSectionLecture>;

  constructor(private db: AngularFirestore, private userStore: UserService) {
    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  //Sections services
  getEditSections(courseId: string): AngularFirestoreCollection<CourseSection> {
    return this.db.collection(this.dbPath + `/${courseId}/sections`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .orderBy("positionOrd", "asc")
    );
  }

  getSections(courseId: string): AngularFirestoreCollection<CourseSection> {
    return this.db.collection(this.dbPath + `/${courseId}/sections`, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("status", "==", 1)
        .orderBy("positionOrd", "asc")
    );
  }

  getSection(courseId: string, id: string) {
    this.sectionsRef = this.db.collection(
      this.dbPath + `/${courseId}/sections`
    );
    return this.sectionsRef.doc(id).valueChanges();
  }

  createSection(section: CourseSection): any {
    section.linkExerciseId = "";
    section.status = 0;
    section.isActive = true;
    section.isDeleted = false;
    section.createdOn = new Date();
    section.createdBy = this.userLogin.uid;
    section.author = this.userLogin;
    section.visibility = this.visibility;

    this.sectionsRef = this.db.collection(
      this.dbPath + `/${section.courseId}/sections`
    );

    return this.sectionsRef.add({ ...section }).then((docRef) => {
      return docRef.id;
    });
  }

  updateSectionInfo(section: CourseSection): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.sectionsRef = this.db.collection(
      this.dbPath + `/${section.courseId}/sections`
    );

    return this.sectionsRef.doc(section.id).update({
      title: section.title,
      status: section.status,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  updateSectionLectures(
    courseId: string,
    id: string,
    lectures: SectionLecture[]
  ): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.sectionsRef = this.db.collection(
      this.dbPath + `/${courseId}/sections`
    );

    return this.sectionsRef.doc(id).update({
      noLecture: lectures ? lectures.length : 0,
      lectures: lectures ? JSON.parse(JSON.stringify(lectures)) : [],
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  updateSectionLinkExercise(
    courseId: string,
    id: string,
    linkExerciseId: string
  ): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.sectionsRef = this.db.collection(
      this.dbPath + `/${courseId}/sections`
    );

    return this.sectionsRef.doc(id).update({
      linkExerciseId: linkExerciseId,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  updateSectionOrder(courseId: string, id: string, pos: number): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.sectionsRef = this.db.collection(
      this.dbPath + `/${courseId}/sections`
    );

    return this.sectionsRef.doc(id).update({
      positionOrd: pos,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  deleteSection(courseId: string, id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.sectionsRef = this.db.collection(
      this.dbPath + `/${courseId}/sections`
    );

    return this.sectionsRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
      author: this.userLogin,
    });
  }

  //Lecture services
  getEditLectures(
    courseId: string,
    sectionId: string
  ): AngularFirestoreCollection<CourseSectionLecture> {
    return this.db.collection(
      this.dbPath + `/${courseId}/sections/${sectionId}/lectures`,
      (ref) =>
        ref
          .where("isDeleted", "==", false)
          .where("isActive", "==", true)
          .orderBy("positionOrd", "asc")
    );
  }

  getLectures(
    courseId: string,
    sectionId: string
  ): AngularFirestoreCollection<CourseSectionLecture> {
    return this.db.collection(
      this.dbPath + `/${courseId}/sections/${sectionId}/lectures`,
      (ref) =>
        ref
          .where("isDeleted", "==", false)
          .where("isActive", "==", true)
          .where("status", "==", 1)
          .orderBy("positionOrd", "asc")
    );
  }

  getLecture(courseId: string, sectionId: string, id: string) {
    this.lecturesRef = this.db.collection(
      this.dbPath + `/${courseId}/sections/${sectionId}/lectures`
    );
    return this.lecturesRef.doc(id).valueChanges();
  }

  createLecture(courseId, sectionId, title, positionOrd): any {
    let lecture = new CourseSectionLecture();
    lecture.courseId = courseId;
    lecture.sectionId = sectionId;
    lecture.title = title;
    lecture.positionOrd = positionOrd;
    lecture.linkExerciseId = "";
    lecture.status = 0;
    lecture.isActive = true;
    lecture.isDeleted = false;
    lecture.createdOn = new Date();
    lecture.createdBy = this.userLogin.uid;
    lecture.author = this.userLogin;
    lecture.lectureContentId = "";
    lecture.sourceType = "";
    lecture.visibility = this.visibility;

    this.lecturesRef = this.db.collection(
      this.dbPath +
        `/${lecture.courseId}/sections/${lecture.sectionId}/lectures`
    );

    return this.lecturesRef.add({ ...lecture }).then((docRef) => {
      lecture.id = docRef.id;
      return lecture;
    });
  }

  updateLectureInfo(lecture: CourseSectionLecture): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.lecturesRef = this.db.collection(
      this.dbPath +
        `/${lecture.courseId}/sections/${lecture.sectionId}/lectures`
    );

    return this.lecturesRef.doc(lecture.id).update({
      title: lecture.title,
      status: lecture.status,
      privacy: lecture.privacy,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  updateLectureContentId(lecture: CourseSectionLecture): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.lecturesRef = this.db.collection(
      this.dbPath +
        `/${lecture.courseId}/sections/${lecture.sectionId}/lectures`
    );

    return this.lecturesRef.doc(lecture.id).update({
      sourceType: lecture.sourceType,
      privacy: lecture.privacy,
      lectureContentId: lecture.lectureContentId,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  updateLectureLinkExercise(lecture: CourseSectionLecture): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.lecturesRef = this.db.collection(
      this.dbPath +
        `/${lecture.courseId}/sections/${lecture.sectionId}/lectures`
    );

    return this.lecturesRef.doc(lecture.id).update({
      linkExerciseId: lecture.linkExerciseId,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  updateLectureOrder(
    courseId: string,
    sectionId: string,
    id: string,
    pos: number
  ): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.lecturesRef = this.db.collection(
      this.dbPath + `/${courseId}/sections/${sectionId}/lectures`
    );

    return this.lecturesRef.doc(id).update({
      positionOrd: pos,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  deleteLecture(lecture: CourseSectionLecture): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    this.lecturesRef = this.db.collection(
      this.dbPath +
        `/${lecture.courseId}/sections/${lecture.sectionId}/lectures`
    );

    return this.lecturesRef.doc(lecture.id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }
}
